// ── Enums / string unions ──────────────────────────────────────────────────────

export type BankChargeWorkflowStatus =
  | 'Draft'
  | 'Submitted'
  | 'PendingApproval'
  | 'Approved'
  | 'Rejected'
  | 'Posted'
  | 'Cancelled';

export type BankChargePostingStatus = 'NotPosted' | 'Posted' | 'Cancelled';

// ── Status display helpers ────────────────────────────────────────────────────

export const BNK_CHARGE_WORKFLOW_STATUS_LABELS: Record<BankChargeWorkflowStatus, string> = {
  Draft:          'Draft',
  Submitted:      'Submitted',
  PendingApproval:'Pending Approval',
  Approved:       'Approved',
  Rejected:       'Rejected',
  Posted:         'Posted',
  Cancelled:      'Cancelled',
};

export const BNK_CHARGE_WORKFLOW_STATUS_COLORS: Record<BankChargeWorkflowStatus, string> = {
  Draft:          'bg-gray-100 text-gray-700',
  Submitted:      'bg-blue-100 text-blue-700',
  PendingApproval:'bg-yellow-100 text-yellow-700',
  Approved:       'bg-green-100 text-green-700',
  Rejected:       'bg-red-100 text-red-700',
  Posted:         'bg-purple-100 text-purple-700',
  Cancelled:      'bg-gray-200 text-gray-500',
};

// ── Detail DTO ────────────────────────────────────────────────────────────────

export interface BankChargeDto {
  id:                     string;
  companyId:              string;
  locationId?:            string | null;
  costCenterId?:          string | null;
  financialYearCode:      string;
  financialPeriodCode:    string;
  chargeNumber:           string;
  chargeDate:             string;
  postingDate:            string;
  bankAccountId:          string;
  bankAccountCode:        string;
  bankAccountName:        string;
  bankGLAccountId?:       string | null;
  chargeType:             string;
  expenseAccountId?:      string | null;
  expenseAccountCode?:    string | null;
  expenseAccountName?:    string | null;
  inputTaxAccountId?:     string | null;
  inputTaxAccountCode?:   string | null;
  inputTaxAccountName?:   string | null;
  currencyCode:           string;
  exchangeRate:           number;
  amount:                 number;
  taxRate:                number;
  taxAmount:              number;
  totalAmount:            number;
  baseAmount:             number;
  reference?:             string | null;
  narration?:             string | null;
  workflowStatus:         BankChargeWorkflowStatus;
  postingStatus:          BankChargePostingStatus;
  submittedBy?:           string | null;
  submittedDate?:         string | null;
  approvedBy?:            string | null;
  approvedDate?:          string | null;
  rejectedBy?:            string | null;
  rejectedDate?:          string | null;
  rejectionReason?:       string | null;
  postedBy?:              string | null;
  postedDate?:            string | null;
  cancelledBy?:           string | null;
  cancelledDate?:         string | null;
  cancellationReason?:    string | null;
  isReversal:             boolean;
  originalChargeId?:      string | null;
  reversalChargeId?:      string | null;
  sourceGlJournalId?:     string | null;
  createdDate:            string;
}

// ── List row DTO ──────────────────────────────────────────────────────────────

export interface BankChargeListDto {
  id:              string;
  chargeNumber:    string;
  chargeDate:      string;
  postingDate:     string;
  bankAccountCode: string;
  bankAccountName: string;
  chargeType:      string;
  currencyCode:    string;
  amount:          number;
  taxAmount:       number;
  totalAmount:     number;
  baseAmount:      number;
  workflowStatus:  BankChargeWorkflowStatus;
  postingStatus:   BankChargePostingStatus;
  reference?:      string | null;
  isReversal:      boolean;
  createdDate:     string;
}

// ── Filter DTO ────────────────────────────────────────────────────────────────

export interface BankChargeFilterDto {
  companyId?:          string;
  locationId?:         string;
  costCenterId?:       string;
  financialYearCode?:  string;
  bankAccountId?:      string;
  chargeType?:         string;
  workflowStatus?:     string;
  postingStatus?:      string;
  dateFrom?:           string;
  dateTo?:             string;
  search?:             string;
  pageNumber?:         number;
  pageSize?:           number;
  sortBy?:             string;
  sortDescending?:     boolean;
}

// ── Create / Update requests ──────────────────────────────────────────────────

export interface CreateBankChargeRequest {
  companyId:            string;
  locationId?:          string | null;
  costCenterId?:        string | null;
  financialYearCode:    string;
  financialPeriodCode:  string;
  chargeDate:           string;
  postingDate:          string;
  bankAccountId:        string;
  chargeType:           string;
  expenseAccountId?:    string | null;
  inputTaxAccountId?:   string | null;
  currencyCode:         string;
  exchangeRate:         number;
  amount:               number;
  taxRate:              number;
  reference?:           string | null;
  narration?:           string | null;
}

export interface UpdateBankChargeRequest {
  chargeDate:           string;
  postingDate:          string;
  bankAccountId:        string;
  bankAccountCode:      string;
  bankAccountName:      string;
  bankGLAccountId?:     string | null;
  chargeType:           string;
  expenseAccountId?:    string | null;
  expenseAccountCode?:  string | null;
  expenseAccountName?:  string | null;
  inputTaxAccountId?:   string | null;
  inputTaxAccountCode?: string | null;
  inputTaxAccountName?: string | null;
  currencyCode:         string;
  exchangeRate:         number;
  amount:               number;
  taxRate:              number;
  reference?:           string | null;
  narration?:           string | null;
}
